<template>
  <div
    class="file-dropzone q-pa-md"
    :class="{ 'dragover': isDragover }"
    @drop.prevent.stop="handleDrop"
    @drageenter.prevent.stop="handleDragenter"
    @dragleave.prevent.stop="handleDragleave"
    @dragover.prevent.stop="handleDragenter"
    @dragend="handleDragleave"
  >
    <label for="fileUpload" class="file-upload-btn">
      <input
        id="fileUpload"
        type="file"
        :accept="acceptForInput"
        @change="handleFileInput"
        multiple
      >
        <q-icon name="cloud_upload" size="1.75rem" />
        <span class="q-mt-sm">{{ $t('upload_document_dropzone_text') }}</span>
    </label>

    <div v-if="hasFiles" class="file-preview">
      <div
        v-for="(file, index) in files"
        :key="file.file.name + '_' + index"
        class="file-preview-element"
      >
        <template>
          <div
            v-if="fileIsImage(file.file)"
            v-show="file.loaded"
            class="image-preview"
            @mouseenter="handleMouseEnter"
          >
            <img :src="file.base64" @load="() => file.loaded = true">
          </div>

          <div
            v-if="fileIsPdf(file.file)"
            v-show="file.loaded"
            class="pdf-preview"
            @mouseenter="handleMouseEnter"
          >
            <pdf :src="file.base64" @loaded="() => file.loaded = true"/>
          </div>
        </template>

        <template v-if="!file.loaded">
          <div class="placeholder-preview">
            <q-spinner />
          </div>
        </template>

        <div class="file-preview-meta">
          <q-btn
            class="file-preview-delete q-pa-xs"
            icon="close"
            size="sm"
            color="red-5"
            flat
            @click="handleDeleteFile(index)"
          />
          <span class="file-preview-filename">{{ file.file.name }}</span>
          <span class="file-preview-filesize">{{ file.humanReadableSize }}</span>
        </div>
      </div>
    </div>

    <div v-if="hasFiles" class="flex justify-center">
      <div>
        <q-btn color="primary" size="sm" @click="handleUpload">
          {{ $t('upload') }}
        </q-btn>
      </div>
    </div>

    <q-inner-loading :visible="isUploading">
      <span class="q-mb-sm">{{ filesToUpload - files.length }} / {{ filesToUpload }}</span>
      <q-spinner size="42px" color="primary" />
    </q-inner-loading>
  </div>
</template>

<script>
  import filesize from 'filesize'
  import pdf from 'vue-pdf'

  const nop = (event) => {
    event.preventDefault()
  }

  export default {
    components: {
      pdf
    },
    data () {
      return {
        isDragover: false,
        isUploading: false,
        files: [],
        filesToUpload: 0
      }
    },
    props: {
      accept: {
        type: Array,
        required: false,
        default: () => ['image/jpeg', 'image/png', 'application/pdf']
      },
      globalDrop: {
        type: Boolean,
        required: false,
        default: true
      },
      uploadFunction: {
        type: Function,
        required: true
      }
    },
    computed: {
      acceptForInput () {
        return this.accept.join(',')
      },
      hasFiles () {
        return this.files.length > 0
      }
    },
    methods: {
      handleFileInput (event) {
        if (this.isUploading) {
          return
        }

        Array.from(event.target.files).forEach(file => {
          this.addFile(file)
        })
      },
      handleDrop (event) {
        event.preventDefault()

        if (this.isUploading) {
          return
        }

        this.isDragover = false
        Array.from(event.dataTransfer.files).forEach(file => {
          this.addFile(file)
        })
      },
      handleDragenter (event) {
        if (this.isUploading) {
          return
        }

        this.isDragover = true
      },
      handleDragleave (event) {
        if (this.isUploading) {
          return
        }

        this.isDragover = false
      },
      handleDeleteFile (index) {
        if (this.isUploading) {
          return
        }

        this.removeFile(index)
      },
      async handleUpload () {
        this.isUploading = true
        let errorOccured = false

        this.filesToUpload = this.files.length

        // Use regular for loop to force synchronous upload
        // and loop in reverse becasue array index keys are changed in loop.
        for (let index = this.files.length - 1; index >= 0; index--) {
          const file = { ...this.files[index] }

          await this.uploadFunction(file)
            .then(() => {
              return this.files.splice(index, 1)
            })
            .catch(() => {
              errorOccured = true
            })
        }

        this.$emit('uploaded')

        this.isUploading = false
        this.isDragover = false

        if (!errorOccured) {
          this.$q.notify({
            type: 'positive',
            message: this.$t('document_upload_successfull')
          })
        } else {
          this.$q.notify({
            type: 'warning',
            message: this.$t('document_upload_unsuccessfull')
          })
        }
      },
      handleMouseEnter (event) {
        event.stopPropagation()

        const startTime = performance.now()
        const scrollDuration = 1500
        const ele = event.target
        let canceled = false

        function easeOutQuad (x) {
          return 1 - (1 - x) * (1 - x)
        }

        const scroll = () => {
          const now = performance.now()
          const elapsed = now - startTime
          const t = elapsed / scrollDuration
          const scrollBy = Math.ceil((ele.scrollHeight - ele.offsetHeight) * easeOutQuad(t))

          if (canceled) {
            return
          }

          ele.scrollTo(0, scrollBy)

          if (t < 1) {
            window.requestAnimationFrame(scroll)
          }
        }

        // Only apply scroll effect when there is enough pixels to scroll
        if ((ele.scrollHeight - ele.offsetHeight) > 20) {
          scroll()

          ele.addEventListener('mouseleave', () => {
            canceled = true

            ele.scrollTo({
              top: 0,
              behavior: 'smooth'
            }, { once: true })
          })
        }
      },
      isValidFileType (file) {
        if (!file) {
          return false
        }

        return this.accept.includes(file.type)
      },
      addFile (file) {
        if (!this.isValidFileType(file)) {
          this.$q.notify({
            type: 'warning',
            message: this.$t('document_upload_invalid_file_type', { fileName: file.name })
          })
          return
        }

        const reader = new FileReader()

        reader.addEventListener('load', () => {
          this.files.push({
            file,
            base64: reader.result,
            humanReadableSize: filesize(file.size),
            loaded: false
          })
        }, false)

        if (file) {
          reader.readAsDataURL(file)
        }
      },
      removeFile (index) {
        this.files.splice(index, 1)
      },
      fileIsImage (file) {
        return ['image/gif', 'image/jpeg', 'image/png']
          .includes(file.type)
      },
      fileIsPdf (file) {
        return ['application/pdf']
          .includes(file.type)
      }
    },
    mounted () {
      if (this.globalDrop) {
        document.addEventListener('drop', this.handleDrop)
        document.addEventListener('dragover', nop)
      }
    },
    beforeDestroy () {
      if (this.globalDrop) {
        document.removeEventListener('drop', this.handleDrop)
        document.removeEventListener('dragover', nop)
      }
    }
  }
</script>

<style lang="stylus" scoped>
  @import '~variables'

  .file-dropzone
    position relative
    height auto
    max-height 350px
    display grid
    grid-template-rows auto 1fr
    grid-row-gap 2rem
    border-radius 4px
    border 3px dashed $grey-4
    transition border .2s ease-in-out
    &.dragover
      border 3px solid $grey-5

    input[type="file"]
      display none

    .file-upload-btn
      height 100%
      display flex
      flex-direction column
      justify-content center
      align-items center
      text-align center
      color #718096
      font-weight bold
      cursor pointer
      &:hover
        color #4a5568

  .file-preview
    display grid
    grid-row-gap .5rem
    overflow-y auto
    grid-template-columns 1fr

    .file-preview-element
      width 100%
      max-width 400px
      height 60px
      display flex
      margin 0 auto
      overflow hidden
      font-size 14px
      color $grey-10
      border-radius 4px
      box-shadow 0 1px 2px 0 rgba(0, 0, 0, 0.05)

      .file-preview-meta
        position relative
        flex 1
        overflow hidden
        padding 10px 26px 8px 8px
        background $grey-2

        .file-preview-filename, .file-preview-filesize
          overflow hidden
          text-overflow ellipsis
          white-space nowrap
          display block

        .file-preview-filename
          font-weight 600
          color $grey-10

        .file-preview-filesize
          color $grey-9

      .file-preview-delete
        position absolute
        top 4px
        right 4px

      .image-preview, .pdf-preview, .placeholder-preview
        flex 0 0 120px
        height 100%
        overflow hidden
        background-color white

      .image-preview
        img
          width 100%
          height auto

      .placeholder-preview
        display flex
        justify-content center
        align-items center
</style>
